// Libraries Import
import { useState } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Grid, Button } from "@mui/material";
import { connect } from 'react-redux';

// Api and utils
import { THEME_MODE } from "../../../constants/Theme";
import { ipRegex } from "../../../lib/utils";

// Component imports
import SettingTextField from "../../../components/SettingTextField";
import SettingSelectField from "../../../components/SettingSelectField";
import SwitchInput from "../../../components/SwitchInput";
import MultiSelectField from "../../../components/SelectizeTextField";

export const MODES = { add: "add", edit: "edit" }

export const PrinterFields = {
    printerName: "name",
    displayName: "displayName",
    connectionType: "connectionType",
    IPAddress: "IPAddress",
    portNumber: "portNumber",
    noOfCopies: "noOfCopies",
    receiptEnabled: "receiptEnabled",
    isOnlineOrderReceiptsEnabled: "isOnlineOrderReceiptsEnabled",
    isLocalReceiptsOnly: "isLocalReceiptsOnly",
    isActive: "isActive",
    coursesIds: "coursesIds",
    floorsIds: "floorsIds",
}

const AddPrinterForm = ({
    open, closeModal, mode, printerData, themeMode, loading, onSubmit, floorList, courseList, courseListMap
}) => {
    const theme = THEME_MODE[themeMode];
    const [errors] = useState(new Map());
    const [changes, setChanges] = useState(new Set());

    // Connection type options
    const connectionTypeOptions = [
        { label: "Ethernet", value: "Ethernet" },
        { label: "USB", value: "USB" }
    ];

    const [state, setState] = useState({
        [PrinterFields.printerName]: printerData ? printerData[PrinterFields.printerName] : "",
        [PrinterFields.displayName]: printerData ? printerData[PrinterFields.displayName] : "",
        [PrinterFields.connectionType]: printerData ? (printerData.isUsbPrinter ? "USB" : "Ethernet") : "Ethernet",
        [PrinterFields.IPAddress]: printerData ? printerData[PrinterFields.IPAddress] : "",
        [PrinterFields.portNumber]: printerData ? printerData[PrinterFields.portNumber] : "",
        [PrinterFields.noOfCopies]: printerData ? printerData[PrinterFields.noOfCopies] : 1,
        [PrinterFields.coursesIds]: printerData ? printerData._idCourses.map((id) => courseListMap.get(id)) : [],
        [PrinterFields.floorsIds]: printerData ? printerData._idFloors ?? [] : [],
        [PrinterFields.receiptEnabled]: printerData ? printerData[PrinterFields.receiptEnabled] : false,
        [PrinterFields.isOnlineOrderReceiptsEnabled]: printerData ? printerData[PrinterFields.isOnlineOrderReceiptsEnabled] : false,
        [PrinterFields.isLocalReceiptsOnly]: printerData ? printerData[PrinterFields.isLocalReceiptsOnly] : false,
        [PrinterFields.isActive]: printerData ? printerData[PrinterFields.isActive] : false,
    });

    const handleInputChange = (field) => (value) => {
        if (field === PrinterFields.portNumber || field === PrinterFields.noOfCopies) {
            value = value.replace(/[^0-9]/g, '');
        }
        
        setState({ ...state, [field]: value });

        // Clear port validation error when switching to USB
        if (field === PrinterFields.connectionType && value === "USB") {
            errors.delete(PrinterFields.portNumber);
        }

        const error = getErrors(field, value);
        if (error) errors.set(field, error);
        else errors.delete(field);

        // Track changes
        const isChanged = hasInputChanged(field, value);
        const newChangeSet = new Set(changes);
        if (isChanged) newChangeSet.add(field);
        else newChangeSet.delete(field);
        setChanges(newChangeSet);
    }

    const getErrors = (field, value) => {
        switch (field) {
            case PrinterFields.printerName:
                if (!value.trim()) return "Printer Name is required.";
                break;
            case PrinterFields.displayName:
                if (!value.trim()) return "Display Name is required.";
                break;
            case PrinterFields.IPAddress:
                if (!value.trim()) return state[PrinterFields.connectionType] === "USB" ? "Printer ID or Path is required." : "IP Address is required.";
                if (state[PrinterFields.connectionType] === "Ethernet" && !ipRegex.test(value)) return "Invalid IP Address.";
                break;
            case PrinterFields.portNumber:
                if (state[PrinterFields.connectionType] === "Ethernet") {
                    if (!value.trim()) return "Port Number is required.";
                    const port = Number(value);
                    if (isNaN(port) || port <= 0 || port > 65535) return "Invalid Port Number."
                }
                break;
            case PrinterFields.noOfCopies:
                if (!value.trim()) return "Number of Copies is required.";
                const copies = Number(value);
                if (isNaN(copies) || copies <= 0 || copies > 99) return "Number of Copies must be between 1 and 99.";
                break;
            default:
                return null;
        }
        return null;
    }

    const hasInputChanged = (field, value) => {
        if (!printerData) return true; // For add mode, any value is a change
        
        switch (field) {
            case PrinterFields.coursesIds:
                if (value.length !== printerData._idCourses.length) return true;
                for (let i = 0; i < value.length; i++) {
                    if (!printerData._idCourses.includes(value[i]._id)) return true;
                }
                return false;
            case PrinterFields.connectionType:
                const originalConnectionType = printerData.isUsbPrinter ? "USB" : "Ethernet";
                return value !== originalConnectionType;
            default:
                const originalValue = printerData?.[field] ?? "";
                return value !== originalValue;
        }
    }

    const isSaveBtnDisabled = (() => {
        if (errors.size > 0 || loading) return true;
        
        if (mode === MODES.add) {
            // For add mode, check if required fields are filled
            const requiredFields = [
                state[PrinterFields.printerName],
                state[PrinterFields.displayName],
                state[PrinterFields.IPAddress],
                state[PrinterFields.noOfCopies]
            ];
            
            // Port is only required for Ethernet connection
            if (state[PrinterFields.connectionType] === "Ethernet") {
                requiredFields.push(state[PrinterFields.portNumber]);
            }
            
            return !requiredFields.every(field => field && field.toString().trim());
        } else {
            // For edit mode, require changes
            return changes.size === 0;
        }
    })();

    const handleSave = () => {
        if (isSaveBtnDisabled) return;
        onSubmit(state, printerData);
    };

    return (
        <Dialog
            open={open}
            onClose={closeModal}
            sx={styles.mainDialog}>
            <Grid p={4}>
                <DialogTitle sx={styles.confirmTitleText}>
                    {`${mode} Printer`}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-slide-description" sx={styles.confirmContentText}>
                        <Grid container spacing={2} p={0}>
                            <Grid item xs={12}>
                                <SettingTextField
                                    value={state[PrinterFields.printerName]}
                                    setValue={handleInputChange(PrinterFields.printerName)}
                                    backgroundColor={theme.inputBg2}
                                    label={"Name"}
                                    labelStyle={styles.labelStyle}
                                    required={true}
                                    variant={"filled"}
                                    error={errors.get(PrinterFields.printerName)}
                                    errorStyles={styles.errorStyle}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <SettingTextField
                                    value={state[PrinterFields.displayName]}
                                    setValue={handleInputChange(PrinterFields.displayName)}
                                    backgroundColor={theme.inputBg2}
                                    label={"Display Name"}
                                    labelStyle={styles.labelStyle}
                                    required={true}
                                    variant={"filled"}
                                    error={errors.get(PrinterFields.displayName)}
                                    errorStyles={styles.errorStyle}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <SettingSelectField
                                    value={state[PrinterFields.connectionType]}
                                    setValue={handleInputChange(PrinterFields.connectionType)}
                                    backgroundColor={theme.inputBg2}
                                    label={"Printer Type"}
                                    labelStyle={styles.labelStyle}
                                    variant={"filled"}
                                    menuList={connectionTypeOptions}
                                    valueField="value"
                                    labelField="label"
                                    placeholder="Select printer type"
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                                <Box mr={2} sx={styles.labelStyle}>
                                    Active
                                </Box>
                                <SwitchInput
                                    checked={state[PrinterFields.isActive]}
                                    onChange={handleInputChange(PrinterFields.isActive)}
                                />
                            </Grid>
                            
                            <Grid item xs={12}>
                                <SettingTextField
                                    value={state[PrinterFields.IPAddress]}
                                    setValue={handleInputChange(PrinterFields.IPAddress)}
                                    backgroundColor={theme.inputBg2}
                                    label={state[PrinterFields.connectionType] === "USB" ? "Printer ID or Path" : "IP Address"}
                                    labelStyle={styles.labelStyle}
                                    required={true}
                                    variant={"filled"}
                                    error={errors.get(PrinterFields.IPAddress)}
                                    errorStyles={styles.errorStyle}
                                />
                            </Grid>
                            
                            {state[PrinterFields.connectionType] === "Ethernet" && (
                                <Grid item xs={12}>
                                    <SettingTextField
                                        value={state[PrinterFields.portNumber]}
                                        setValue={handleInputChange(PrinterFields.portNumber)}
                                        backgroundColor={theme.inputBg2}
                                        label={"Port Number"}
                                        labelStyle={styles.labelStyle}
                                        required={true}
                                        variant={"filled"}
                                        type={"number"}
                                        error={errors.get(PrinterFields.portNumber)}
                                        errorStyles={styles.errorStyle}
                                    />
                                </Grid>
                            )}

                            <Grid item xs={12}>
                                <SettingTextField
                                    value={state[PrinterFields.noOfCopies]}
                                    setValue={handleInputChange(PrinterFields.noOfCopies)}
                                    backgroundColor={theme.inputBg2}
                                    label={"Number of Copies"}
                                    labelStyle={styles.labelStyle}
                                    required={true}
                                    variant={"filled"}
                                    type={"number"}
                                    error={errors.get(PrinterFields.noOfCopies)}
                                    errorStyles={styles.errorStyle}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <MultiSelectField
                                    tagColor={theme.inputBg5}
                                    value={state[PrinterFields.coursesIds]}
                                    onChange={handleInputChange(PrinterFields.coursesIds)}
                                    backgroundColor={theme.inputBg2}
                                    options={courseList}
                                    variant={"filled"}
                                    label={"Assign Courses"}
                                    labelStyle={styles.labelStyle}
                                    placeholder={state[PrinterFields.coursesIds].length ? "" : "Enter here"}
                                    inputContainerStyle={{
                                        height: "auto",
                                        minHeight: "100px",
                                        alignItems: "flex-start",
                                        paddingTop: "16px",
                                        paddingBottom: "16px"
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <MultiSelectField
                                    tagColor={theme.inputBg5}
                                    value={state[PrinterFields.floorsIds]}
                                    onChange={handleInputChange(PrinterFields.floorsIds)}
                                    backgroundColor={theme.inputBg2}
                                    options={floorList}
                                    variant={"filled"}
                                    label={"Link Floors"}
                                    labelStyle={{ fontSize: 14, fontFamily: "GilroySemiBold", textAlign: "left" }}
                                    placeholder={state[PrinterFields.floorsIds].length ? "" : "Enter here"}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                                <Box mr={2} sx={styles.labelStyle}>
                                    Receipt Enabled
                                </Box>
                                <SwitchInput
                                    checked={state[PrinterFields.receiptEnabled]}
                                    onChange={handleInputChange(PrinterFields.receiptEnabled)}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                                <Box mr={2} sx={styles.labelStyle}>
                                    Online Receipt
                                </Box>
                                <SwitchInput
                                    checked={state[PrinterFields.isOnlineOrderReceiptsEnabled]}
                                    onChange={handleInputChange(PrinterFields.isOnlineOrderReceiptsEnabled)}
                                />
                            </Grid>

                            <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start" }}>
                                <Box mr={2} sx={styles.labelStyle}>
                                    Table Messages
                                </Box>
                                <SwitchInput
                                    checked={state[PrinterFields.isLocalReceiptsOnly]}
                                    onChange={handleInputChange(PrinterFields.isLocalReceiptsOnly)}
                                />
                            </Grid>
                        </Grid>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={styles.confirmBtnDialog}>
                    <Button sx={[styles.btnText, styles.noBtnText]}
                        onClick={closeModal}>
                        cancel
                    </Button>
                    <Button sx={[styles.btnText, styles.yesBtnText]}
                        disabled={isSaveBtnDisabled}
                        onClick={handleSave}>
                        {loading ? "Processing..." : `${mode} Printer`}
                    </Button>
                </DialogActions>
            </Grid>
        </Dialog >
    )
}

const mapStateToProps = state => ({
    themeMode: state.userData.themeMode
})
export default connect(mapStateToProps, null)(AddPrinterForm);

const styles = {
    mainDialog: {
        '& .MuiDialog-paper': {
            borderRadius: 3,
            xs: {
                width: 'calc(100% - 2rem)',
            },
            sm: {
                width: '36rem'
            },
        },
    },
    labelStyle: {
        fontSize: 14,
        fontFamily: "GilroySemiBold",
        textAlign: "left",
        color: "#272D2F",
    },
    errorStyle: {
        textAlign: 'left',
        fontSize: '0.75rem',
    },
    confirmTitleText: {
        fontFamily: 'GilroyBold',
        textAlign: 'center',
        fontSize: '1.5rem',
        textTransform: 'capitalize'
    },
    confirmContentText: {
        fontFamily: 'GilroyMedium',
        textAlign: 'center',
    },
    confirmBtnDialog: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '1.5rem'
    },
    btnText: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        textTransform: 'capitalize',
        fontFamily: "GilroySemiBold",
        height: "2.5rem",
        padding: "0 18px",
        fontSize: "14px",
        cursor: "pointer",
        border: "2px solid #000000",
        borderRadius: "8px",
        flex: 1
    },
    noBtnText: {
        color: '#000000',
        '&:hover': {
            backgroundColor: '#ffffff !important',
        },
    },
    yesBtnText: {
        backgroundColor: '#000000',
        color: '#ffffff !important',
        borderRadius: 2,
        px: 2,
        '&:hover': {
            color: '#ffffff !important',
            backgroundColor: '#000000 !important',
        },
        '&:disabled': {
            opacity: 0.5
        }
    }
}
